<template>
  <span
    ref="trigger"
    class="ca-deferred-tip-trigger"
    @mouseenter="open"
    @mouseleave="close"
    @focus="open"
    @blur="close"
  >
    <slot />
  </span>
</template>

<script>
import { mapGetters } from 'vuex'

const TIP_CLASS = 'ca-deferred-tip'

export default {
  props: {
    /** Room name of the complaint that was deferred. */
    roomName: { type: String, default: '' },
    /** Whether the deferred complaint is still open. */
    isOpen: { type: Boolean, default: false },
    /** Names of the other rooms the cleaner serviced first. */
    rooms: { type: Array, default: () => [] },
  },
  computed: {
    ...mapGetters(['isRTL']),
  },
  beforeDestroy() {
    this.close()
  },
  methods: {
    open() {
      if (!this.$refs.trigger) return
      this.close()

      const rect = this.$refs.trigger.getBoundingClientRect()
      const tipEl = this.buildTipElement()
      document.body.appendChild(tipEl)

      const margin = 8
      const tipRect = tipEl.getBoundingClientRect()
      let top = rect.bottom + margin
      let left = rect.left + rect.width / 2 - tipRect.width / 2
      left = Math.max(margin, Math.min(left, window.innerWidth - tipRect.width - margin))
      if (top + tipRect.height > window.innerHeight - margin) {
        top = rect.top - tipRect.height - margin
      }
      if (top < margin) top = margin

      tipEl.style.top = `${Math.round(top)}px`
      tipEl.style.left = `${Math.round(left)}px`

      this._tipEl = tipEl
      this._onDismiss = () => this.close()
      window.addEventListener('scroll', this._onDismiss, true)
      window.addEventListener('resize', this._onDismiss)
    },
    buildTipElement() {
      const tipEl = document.createElement('div')
      tipEl.className = TIP_CLASS
      tipEl.setAttribute('role', 'tooltip')
      tipEl.setAttribute('dir', this.isRTL ? 'rtl' : 'ltr')

      const head = document.createElement('div')
      head.className = `${TIP_CLASS}__head`
      const headIcon = document.createElement('span')
      headIcon.className = `${TIP_CLASS}__head-icon`
      headIcon.setAttribute('aria-hidden', 'true')
      headIcon.textContent = '↪'
      head.appendChild(headIcon)
      head.appendChild(document.createTextNode(this.$t('ca_deferred_tip_title')))
      tipEl.appendChild(head)

      const roomRow = document.createElement('div')
      roomRow.className = `${TIP_CLASS}__room`
      const roomName = document.createElement('span')
      roomName.className = `${TIP_CLASS}__room-name`
      roomName.textContent = this.roomName
      roomRow.appendChild(roomName)
      const status = document.createElement('span')
      status.className = `${TIP_CLASS}__status ${this.isOpen ? 'is-open' : 'is-closed'}`
      status.textContent = this.isOpen
        ? this.$t('complaint_still_open')
        : this.$t('ca_deferred_tip_resolved')
      roomRow.appendChild(status)
      tipEl.appendChild(roomRow)

      const desc = document.createElement('div')
      desc.className = `${TIP_CLASS}__desc`
      desc.textContent = this.isOpen
        ? this.$t('ca_deferred_tip_desc_open')
        : this.$t('ca_deferred_tip_desc_closed')
      tipEl.appendChild(desc)

      const roomsLabel = document.createElement('div')
      roomsLabel.className = `${TIP_CLASS}__rooms-label`
      roomsLabel.textContent = this.$t('ca_deferred_tip_rooms_label')
      tipEl.appendChild(roomsLabel)

      const roomsList = document.createElement('ul')
      roomsList.className = `${TIP_CLASS}__rooms`
      ;(this.rooms || []).forEach((name) => {
        const item = document.createElement('li')
        item.textContent = name
        roomsList.appendChild(item)
      })
      tipEl.appendChild(roomsList)

      return tipEl
    },
    close() {
      if (this._tipEl) {
        this._tipEl.remove()
        this._tipEl = null
      }
      if (this._onDismiss) {
        window.removeEventListener('scroll', this._onDismiss, true)
        window.removeEventListener('resize', this._onDismiss)
        this._onDismiss = null
      }
    },
  },
}
</script>

<style lang="scss">
.ca-deferred-tip-trigger {
  display: inline-flex;
  vertical-align: middle;
}

.ca-deferred-tip {
  position: fixed;
  z-index: 10050;
  width: max-content;
  max-width: min(280px, calc(100vw - 16px));
  padding: 10px 12px;
  border-radius: 8px;
  background-color: #2c3e50;
  color: #fff;
  font-size: 0.76rem;
  line-height: 1.35;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.28);
  pointer-events: none;
  text-align: start;

  &__head {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.78rem;
    font-weight: 700;
    color: #fff;
  }

  &__head-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background-color: #e67e22;
    color: #fff;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1;
    flex-shrink: 0;
  }

  &__room {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 6px;
  }

  &__room-name {
    font-weight: 700;
    font-size: 0.82rem;
  }

  &__status {
    padding: 1px 6px;
    border-radius: 999px;
    font-size: 0.64rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;

    &.is-open {
      background: rgba(230, 126, 34, 0.25);
      color: #f0b27a;
    }

    &.is-closed {
      background: rgba(95, 155, 70, 0.25);
      color: #9ed286;
    }
  }

  &__desc {
    margin-top: 6px;
    color: rgba(255, 255, 255, 0.82);
  }

  &__rooms-label {
    margin-top: 8px;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: rgba(255, 255, 255, 0.6);
  }

  &__rooms {
    margin: 4px 0 0;
    padding-inline-start: 16px;

    li {
      font-weight: 600;
      line-height: 1.4;
    }
  }
}
</style>
